/**
 * Field flattening and collection utilities
 */

import { collectFieldsByType } from '@core-engine/schema/field-helpers/walking.js'
import type { SchemaField } from '@core-engine/schema/types.js'

// ============================================================================
// Field Flattening
// ============================================================================

const AUTO_ID_FIELD: SchemaField = {
  name: 'id',
  type: 'uuid',
  primaryKey: true
}

/**
 * Ensure fields have an ID field - automatically injects one if not present
 */
export function ensureIdField(fields: SchemaField[]): SchemaField[] {
  const hasId = fields.some((f) => f.primaryKey || f.name === 'id')
  return hasId ? fields : [AUTO_ID_FIELD, ...fields]
}

/**
 * Flatten group and tabs fields into individual fields for database operations
 * Automatically ensures an ID field exists
 */
export function flattenFields(fields: SchemaField[]): SchemaField[] {
  return flattenFieldsWithoutIdCheck(ensureIdField(fields))
}

function flattenFieldsWithoutIdCheck(fields: SchemaField[]): SchemaField[] {
  const flattened: SchemaField[] = []

  for (const field of fields) {
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      flattened.push(...flattenFieldsWithoutIdCheck(field.fields))
    } else if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (tab.fields) {
          flattened.push(...flattenFieldsWithoutIdCheck(tab.fields))
        }
      }
    } else {
      flattened.push(field)
    }
  }

  return flattened
}

// ============================================================================
// Field Collection
// ============================================================================

/**
 * Get all many-to-many relationship fields
 */
export function getManyToManyFields(fields: SchemaField[]): SchemaField[] {
  const flat = flattenFields(fields)
  return flat.filter((f) => f.type === 'relationship' && f.multiple === true && f.relationship)
}

/**
 * Get all single relationship fields (excluding many-to-many)
 */
export function getNestedRelationshipFields(fields: SchemaField[]): SchemaField[] {
  const result: SchemaField[] = []

  function collect(fieldsToCheck: SchemaField[]): void {
    for (const f of fieldsToCheck) {
      if (f.type === 'relationship' && f.relationship && !f.multiple) {
        result.push(f)
      }
      if (f.type === 'group' && f.fields) collect(f.fields)
      if (f.type === 'list' && f.fields) collect(f.fields)
      if (f.type === 'tabs' && f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) collect(tab.fields)
        }
      }
    }
  }

  collect(fields)
  return result
}

/**
 * Collect relationship fields from top-level and groups only (not lists)
 */
export function collectRelationshipFieldsTopLevel(fields: SchemaField[]): SchemaField[] {
  const result: SchemaField[] = []

  function collect(fieldsToCheck: SchemaField[]): void {
    for (const f of fieldsToCheck) {
      if (f.type === 'relationship' && f.relationship) {
        result.push(f)
      }
      if (f.type === 'group' && f.fields) {
        collect(f.fields)
      }
    }
  }

  collect(fields)
  return result
}

/**
 * Collect ALL relationship fields including those in lists
 */
export function collectAllRelationshipFields(fields: SchemaField[]): SchemaField[] {
  return collectFieldsByType(fields, 'relationship')
}

/**
 * Get list fields that have nested fields (not just items)
 */
export function getListFieldsWithNestedFields(fields: SchemaField[]): SchemaField[] {
  const result: SchemaField[] = []

  function collect(fieldsToCheck: SchemaField[]): void {
    for (const f of fieldsToCheck) {
      if (f.type === 'list' && f.fields && f.fields.length > 0) {
        result.push(f)
      }
      if (f.type === 'group' && f.fields) {
        collect(f.fields)
      }
    }
  }

  collect(fields)
  return result
}

/**
 * Get nested list fields (list fields inside list fields)
 */
export function getNestedListFields(
  fields: SchemaField[]
): { parent: SchemaField; nested: SchemaField }[] {
  const result: { parent: SchemaField; nested: SchemaField }[] = []

  const listFields = getListFieldsWithNestedFields(fields)
  for (const parent of listFields) {
    if (parent.fields) {
      for (const nested of parent.fields) {
        if (nested.type === 'list' && nested.fields && nested.fields.length > 0) {
          result.push({ parent, nested })
        }
      }
    }
  }

  return result
}
